import DOMPurify from 'dompurify';
import type { MailAttachment } from '@jerome2/shared';

// Mail-HTML veilig maken voor de lezer: scripts en formulieren weg, inline afbeeldingen (cid:)
// via de bijlage-proxy, externe afbeeldingen geblokkeerd tot Jeroen ze toont.

export interface SafeHtml {
  html: string;
  blockedImages: number;
}

export function attachmentUrl(messageId: string, attachmentId: string, download = false): string {
  return `/api/mail/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}${download ? '?download=1' : ''}`;
}

export function sanitizeMailHtml(html: string, messageId: string, attachments: MailAttachment[], opts: { showExternal?: boolean } = {}): SafeHtml {
  const byCid = new Map(attachments.filter((a) => a.contentId).map((a) => [a.contentId!.replace(/^<|>$/g, ''), a.id]));
  let blocked = 0;
  const purifier = DOMPurify();
  purifier.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src' || data.attrName === 'background' || (data.attrName === 'srcset' && node.tagName === 'IMG')) {
      const v = data.attrValue.trim();
      if (v.startsWith('cid:')) {
        const id = byCid.get(v.slice(4).replace(/^<|>$/g, ''));
        if (id) data.attrValue = attachmentUrl(messageId, id);
        else data.keepAttr = false;
      } else if (/^https?:/i.test(v)) {
        if (!opts.showExternal) {
          blocked += 1;
          data.keepAttr = false;
          node.setAttribute('data-blocked-src', v);
        }
      } else if (!v.startsWith('data:image/') && !v.startsWith('/api/')) data.keepAttr = false;
    }
    if (data.attrName === 'style' && /url\(/i.test(data.attrValue) && !opts.showExternal) {
      data.attrValue = data.attrValue.replace(/url\([^)]*\)/gi, 'none');
    }
  });
  purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  const clean = purifier.sanitize(html, { WHOLE_DOCUMENT: false, FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link', 'base'], FORBID_ATTR: ['onerror', 'onload'], ADD_ATTR: ['target'] });
  return { html: clean, blockedImages: blocked };
}

export function textAsHtml(text: string): string {
  const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Complete pagina voor het afgeschermde iframe: eigen basisstijl, geen externe bronnen. */
export function frameDocument(bodyHtml: string): string {
  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: 'self' https: http:; style-src 'unsafe-inline'; font-src data:;"><style>
  :root{color-scheme:light}
  body{margin:0;padding:4px 0 8px;font:15px/1.55 Inter,system-ui,-apple-system,'Segoe UI',sans-serif;color:#03153f;background:#fff;word-wrap:break-word;overflow-wrap:anywhere;overflow-x:auto}
  img{max-width:100%;height:auto}
  blockquote{margin:12px 0;padding-left:12px;border-left:3px solid #d5e0f0;color:#2f4270}
  table{max-width:100%;border-collapse:collapse}
  a{color:#073889}
  pre{white-space:pre-wrap}
  #jerome-wrap{display:flow-root}
  </style></head><body><div id="jerome-wrap">${bodyHtml}</div></body></html>`;
}
